import { useEffect, useState } from "react";
import { FormatDateTimeDDMMYYHHMMSS } from "../../Logic/DateTimeFormatters";
import { Departments } from "../../Logic/EmsAPI/mailboxAccessRequest";
import Confirm from "../Generic/ConfirmDialog";
import "./styles.scss";
import MailboxAccessRequestDetailsModal from "./MailboxAccessRequestDetailsModal";
import {
    getTenantDepartments,
    MailboxAccessRequest,
    MailboxesAccessRequests,
    requestStatus,
    UpdateMailboxAccessRequestStatus,
} from "./tenantApiCall";
import { useMailboxAccessRequestsList } from "./useMailboxAccessRequestsList";

export interface MailboxAccressRequestsForManagerProps {
    onShowMyRequests: () => void;
}

function MailboxAccressRequestsForManager({ onShowMyRequests }: MailboxAccressRequestsForManagerProps) {
    const [departments, setDepartments] = useState<Array<Departments>>([]);
    const [selectedRequest, setSelectedRequest] = useState<MailboxAccessRequest>();
    const { isLoading, requests, loadData } = useMailboxAccessRequestsList(MailboxesAccessRequests);
    const [actionRequest, setActionRequest] = useState<MailboxAccessRequest>();
    const [newStatusId, setNewStatusId] = useState<number>(0);
    const [declineReason, setDeclineReason] = useState<string>("");

    useEffect(() => {
        (async () => {
            await loadData();
        })();
        (async () => {
            setDepartments(await getTenantDepartments());
        })();
    }, []);

    function closeActionModal() {
        setActionRequest(undefined);
        setNewStatusId(0);
        setDeclineReason("");
    }

    async function updateRequestStatus(
        request: MailboxAccessRequest,
        statusId: number,
        reason: string | null = null,
    ): Promise<boolean> {
        const result = await UpdateMailboxAccessRequestStatus(request.userMailboxAccessRequestID, {
            id: request.userMailboxAccessRequestID,
            accessRequestStatusID: statusId,
            userID: request.userID,
            declineReason: reason,
        });
        if (result.isOk) {
            await loadData();
            return true;
        }
        alert("Failed to update mailbox access request.");
        return false;
    }

    async function submitActionRequest() {
        if (!actionRequest) return;

        const succeeded = await updateRequestStatus(
            actionRequest,
            newStatusId,
            newStatusId === requestStatus.Declined ? declineReason : null,
        );
        if (succeeded) {
            closeActionModal();
        }
    }

    const isActionValid =
        newStatusId > 0 && (newStatusId !== requestStatus.Declined || declineReason.trim().length > 0);

    const closedStatuses = [requestStatus.Approved, requestStatus.Declined, requestStatus.Deleted];
    const openRequests = requests.filter(request => !closedStatuses.includes(request.accessRequestStatusID ?? 0));
    const closedRequests = requests.filter(request => closedStatuses.includes(request.accessRequestStatusID ?? 0));

    function renderActionButtons(request: MailboxAccessRequest) {
        return (
            <>
                <button
                    className="g-btn-alternate"
                    onClick={e => {
                        e.stopPropagation();
                        updateRequestStatus(request, requestStatus.Approved);
                    }}
                >
                    Approve
                </button>
                &nbsp; &nbsp; &nbsp; &nbsp;
                <button
                    className="g-btn-error"
                    onClick={e => {
                        e.stopPropagation();
                        setActionRequest(request);
                        setNewStatusId(requestStatus.Declined);
                    }}
                >
                    Decline
                </button>
            </>
        );
    }

    return (
        <>
            <div className="header">
                Mailbox Access Requests
                <div style={{ textAlign: "right", float: "right", marginRight: "10px" }}>
                    <button
                        className="g-btn-action"
                        style={{ fontSize: "9pt", textAlign: "right" }}
                        onClick={onShowMyRequests}
                    >
                        {" "}
                        My Mailbox Access Requests
                    </button>
                </div>
            </div>
            <div style={{ padding: "20px", paddingTop: "13px" }}>
                {isLoading && <div>Loading...</div>}
                {!isLoading && (
                    <table className="roles-table">
                        <thead>
                            <tr>
                                <td colSpan={8}>
                                    <h5>Open Requests</h5>
                                </td>
                            </tr>

                            <tr>
                                <th>Requested Date</th>
                                <th>Mailbox</th>
                                <th>Request For</th>
                                <th>Requested Role</th>
                                <th>Status</th>
                                <th>Updated Date</th>
                                <th>Updated By</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {openRequests.length === 0 && (
                                <tr>
                                    <td colSpan={8}>No mailbox access requests found.</td>
                                </tr>
                            )}
                            {openRequests.map(request => (
                                <tr
                                    key={request.userMailboxAccessRequestID}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setSelectedRequest(request)}
                                >
                                    <td>
                                        {request.dateCreated ? FormatDateTimeDDMMYYHHMMSS(request.dateCreated) : ""}
                                    </td>
                                    <td>{request.mailboxName}</td>
                                    <td>{request.userName}</td>
                                    <td>{request.roleName}</td>
                                    <td>{request.accessRequestStatusName}</td>
                                    <td>
                                        {request.updatedAt ? FormatDateTimeDDMMYYHHMMSS(request.updatedAt) : ""}
                                    </td>
                                    <td>
                                        {request.updateUser
                                            ? `${request.updateUser.firstname ?? ""} ${
                                                  request.updateUser.lastname ?? ""
                                              }`.trim()
                                            : ""}
                                    </td>
                                    <td>{renderActionButtons(request)}</td>
                                </tr>
                            ))}

                            <tr>
                                <td
                                    colSpan={8}
                                    style={{ backgroundColor: "#fff" }}
                                >
                                    <br />
                                </td>
                            </tr>

                            <tr>
                                <td colSpan={8}>
                                    <h5>Actioned Requests</h5>
                                </td>
                            </tr>

                            <tr>
                                <th>Requested Date</th>
                                <th>Mailbox</th>
                                <th>Request For</th>
                                <th>Requested Role</th>
                                <th>Status</th>
                                <th>Updated Date</th>
                                <th>Updated By</th>
                                <th></th>
                            </tr>

                            {closedRequests.length === 0 && (
                                <tr>
                                    <td colSpan={8}>No mailbox access requests found.</td>
                                </tr>
                            )}
                            {closedRequests.map(request => (
                                <tr
                                    key={request.userMailboxAccessRequestID}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setSelectedRequest(request)}
                                >
                                    <td>
                                        {request.dateCreated ? FormatDateTimeDDMMYYHHMMSS(request.dateCreated) : ""}
                                    </td>
                                    <td>{request.mailboxName}</td>
                                    <td>{request.userName}</td>
                                    <td>{request.roleName}</td>
                                    <td>{request.accessRequestStatusName}</td>
                                    <td>
                                        {request.updatedAt ? FormatDateTimeDDMMYYHHMMSS(request.updatedAt) : ""}
                                    </td>
                                    <td>
                                        {request.updateUser
                                            ? `${request.updateUser.firstname ?? ""} ${
                                                  request.updateUser.lastname ?? ""
                                              }`.trim()
                                            : ""}
                                    </td>
                                    <td>&nbsp;</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            {selectedRequest && (
                <MailboxAccessRequestDetailsModal
                    request={selectedRequest}
                    departments={departments}
                    onClose={() => setSelectedRequest(undefined)}
                />
            )}
            {actionRequest && (
                <Confirm
                    title="Reason for Decline"
                    onConfirm={submitActionRequest}
                    onCancel={closeActionModal}
                    isValid={isActionValid}
                >
                    <div style={{ width: "400px" }}>
                        {newStatusId === requestStatus.Declined && (
                            <div
                                className="row  "
                                style={{ marginTop: "-15px" }}
                            >
                                <div className="col-sm-12">
                                    <textarea
                                        id="declineReason"
                                        className="g-TextArea-default"
                                        value={declineReason}
                                        onChange={e => setDeclineReason(e.currentTarget.value)}
                                        maxLength={512}
                                        style={{ width: "400px", minWidth: "400px" }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </Confirm>
            )}
        </>
    );
}
export default MailboxAccressRequestsForManager;
